import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  UserPlus, KeyRound, Ban, CheckCircle2, Users, Loader2, BookOpen, Plus, BarChart3,
  GraduationCap, Presentation, Globe, Layers, TrendingUp, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CourseEditor from "@/components/CourseEditor";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

const initials = (name = "") => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";

function SectionHeader({ icon: Icon, title, sub }) {
  return (
    <div>
      <h1 className="font-heading text-4xl font-extrabold tracking-tight flex items-center gap-3 text-primary">
        <span className="h-11 w-11 rounded-2xl bg-primary/5 ring-1 ring-secondary/30 flex items-center justify-center">
          <Icon className="h-6 w-6 text-secondary" />
        </span>
        {title}
      </h1>
      <p className="text-muted-foreground mt-2 text-base">{sub}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ username: "", name: "", password: "", role: "student", native_language: "es", course_id: "none" });
  const [courseOpen, setCourseOpen] = useState(false);
  const [courseForm, setCourseForm] = useState({ title: "", description: "", level: "A1", language: "en" });
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [editCourseId, setEditCourseId] = useState(null);

  const teachers = users.filter((u) => u.role === "teacher");
  const students = users.filter((u) => u.role === "student");

  const load = async () => {
    try {
      const [u, c, p] = await Promise.all([
        api.get("/admin/users"), api.get("/courses"), api.get("/admin/progress"),
      ]);
      setUsers(u.data); setCourses(c.data); setProgress(p.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", { ...form, course_id: form.course_id === "none" ? null : form.course_id });
      toast.success(t("admin.create_user"));
      setCreateOpen(false);
      setForm({ username: "", name: "", password: "", role: "student", native_language: "es", course_id: "none" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const createCourse = async (e) => {
    e.preventDefault();
    try {
      await api.post("/courses", courseForm);
      toast.success(t("admin.create_course"));
      setCourseOpen(false);
      setCourseForm({ title: "", description: "", level: "A1", language: "en" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const doReset = async () => {
    try {
      await api.post(`/admin/users/${resetTarget.id}/reset-password`, { new_password: newPassword });
      toast.success("OK"); setResetTarget(null); setNewPassword("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleStatus = async (u) => {
    const action = u.status === "suspended" ? "reactivate" : "suspend";
    try { await api.post(`/admin/users/${u.id}/${action}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const assignTeacher = async (courseId, teacherId) => {
    try { await api.post(`/admin/courses/${courseId}/assign-teacher`, { user_id: teacherId }); toast.success("OK"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const assignStudent = async (courseId, studentId) => {
    try { await api.post(`/admin/courses/${courseId}/assign-student`, { user_id: studentId }); toast.success("OK"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const tabCls = "rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-5 py-2.5 font-semibold";

  return (
    <AppLayout>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <Tabs defaultValue="users" className="space-y-8">
          <TabsList className="rounded-full bg-muted p-1.5 h-auto">
            <TabsTrigger value="users" data-testid="tab-users" className={tabCls}><Users className="h-4 w-4 mr-2" />{t("nav.users")}</TabsTrigger>
            <TabsTrigger value="courses" data-testid="tab-courses" className={tabCls}><BookOpen className="h-4 w-4 mr-2" />{t("nav.courses")}</TabsTrigger>
            <TabsTrigger value="progress" data-testid="tab-progress" className={tabCls}><BarChart3 className="h-4 w-4 mr-2" />{t("nav.progress")}</TabsTrigger>
          </TabsList>

          {/* USERS */}
          <TabsContent value="users" className="space-y-7">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <SectionHeader icon={Users} title={t("admin.users_title")} sub={t("admin.users_sub")} />
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="open-create-user-button" size="lg" className="rounded-full font-bold h-12 px-6"><UserPlus className="h-5 w-5 mr-2" />{t("admin.create_user")}</Button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl">
                  <DialogHeader><DialogTitle className="text-2xl">{t("admin.create_user")}</DialogTitle></DialogHeader>
                  <form onSubmit={createUser} className="space-y-4">
                    <div className="space-y-2"><Label>{t("common.username")}</Label>
                      <Input data-testid="create-username-input" required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="rounded-xl h-11" /></div>
                    <div className="space-y-2"><Label>{t("common.name")}</Label>
                      <Input data-testid="create-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl h-11" /></div>
                    <div className="space-y-2"><Label>{t("common.password")}</Label>
                      <Input data-testid="create-password-input" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl h-11" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>{t("common.role")}</Label>
                        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                          <SelectTrigger data-testid="create-role-select" className="rounded-xl h-11"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="student">{t("common.student")}</SelectItem>
                            <SelectItem value="teacher">{t("common.teacher")}</SelectItem>
                          </SelectContent></Select></div>
                      <div className="space-y-2"><Label>{t("admin.native")}</Label>
                        <Select value={form.native_language} onValueChange={(v) => setForm({ ...form, native_language: v })}>
                          <SelectTrigger className="rounded-xl h-11"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="es">ES</SelectItem><SelectItem value="en">EN</SelectItem></SelectContent></Select></div>
                    </div>
                    {form.role === "student" && (
                      <div className="space-y-2"><Label>{t("nav.courses")} ({t("common.optional")})</Label>
                        <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
                          <SelectTrigger data-testid="create-course-select" className="rounded-xl h-11"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{t("common.none")}</SelectItem>
                            {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                          </SelectContent></Select></div>
                    )}
                    <DialogFooter><Button type="submit" data-testid="submit-create-user-button" className="rounded-full h-11 px-6">{t("common.create")}</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-secondary" /></div> : (
              <div data-testid="users-table" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {users.map((u) => {
                  const RoleIcon = u.role === "teacher" ? Presentation : GraduationCap;
                  return (
                    <Card key={u.id} data-testid={`user-row-${u.username}`} className="rounded-[1.4rem] p-6 border-border shadow-[0_6px_24px_rgb(20,38,77,0.05)] hover:-translate-y-1 transition-transform">
                      <div className="flex items-start gap-4">
                        <div className="h-14 w-14 rounded-2xl bg-primary text-primary-foreground font-heading font-bold text-lg flex items-center justify-center shrink-0 ring-2 ring-secondary/40">
                          {initials(u.name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-heading font-bold text-lg truncate">{u.name}</h3>
                          <p className="text-sm text-muted-foreground truncate">@{u.username}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className="rounded-full bg-secondary/15 text-secondary-foreground border-0 capitalize font-semibold"><RoleIcon className="h-3.5 w-3.5 mr-1" />{t(`common.${u.role}`)}</Badge>
                            <Badge data-testid={`status-badge-${u.username}`} variant={u.status === "suspended" ? "destructive" : "secondary"}
                              className={`rounded-full font-semibold ${u.status !== "suspended" ? "bg-emerald-100 text-emerald-700 border-0" : ""}`}>
                              {t(`common.${u.status}`)}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-5 pt-4 border-t border-border">
                        <Button size="sm" variant="outline" className="rounded-full flex-1" data-testid={`reset-password-button-${u.username}`} onClick={() => { setResetTarget(u); setNewPassword(""); }}>
                          <KeyRound className="h-4 w-4 mr-1" />{t("common.reset")}
                        </Button>
                        <Button size="sm" variant={u.status === "suspended" ? "default" : "destructive"} className="rounded-full flex-1" data-testid={`toggle-status-button-${u.username}`} onClick={() => toggleStatus(u)}>
                          {u.status === "suspended" ? <><CheckCircle2 className="h-4 w-4 mr-1" />{t("common.reactivate")}</> : <><Ban className="h-4 w-4 mr-1" />{t("common.suspend")}</>}
                        </Button>
                      </div>
                    </Card>
                  );
                })}
                {users.length === 0 && <p className="text-muted-foreground col-span-full text-center py-10">—</p>}
              </div>
            )}
          </TabsContent>

          {/* COURSES */}
          <TabsContent value="courses" className="space-y-7">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <SectionHeader icon={Layers} title={t("admin.courses_title")} sub={t("admin.courses_sub")} />
              <Dialog open={courseOpen} onOpenChange={setCourseOpen}>
                <DialogTrigger asChild><Button data-testid="open-create-course-button" size="lg" className="rounded-full font-bold h-12 px-6"><Plus className="h-5 w-5 mr-2" />{t("admin.create_course")}</Button></DialogTrigger>
                <DialogContent className="rounded-2xl">
                  <DialogHeader><DialogTitle className="text-2xl">{t("admin.create_course")}</DialogTitle></DialogHeader>
                  <form onSubmit={createCourse} className="space-y-4">
                    <div className="space-y-2"><Label>{t("common.title")}</Label>
                      <Input data-testid="course-title-input" required value={courseForm.title} onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })} className="rounded-xl h-11" /></div>
                    <div className="space-y-2"><Label>{t("common.description")}</Label>
                      <Textarea value={courseForm.description} onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })} className="rounded-xl" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>{t("common.level")}</Label>
                        <Select value={courseForm.level} onValueChange={(v) => setCourseForm({ ...courseForm, level: v })}>
                          <SelectTrigger data-testid="course-level-select" className="rounded-xl h-11"><SelectValue /></SelectTrigger>
                          <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select></div>
                      <div className="space-y-2"><Label>{t("common.language")}</Label>
                        <Select value={courseForm.language} onValueChange={(v) => setCourseForm({ ...courseForm, language: v })}>
                          <SelectTrigger data-testid="course-language-select" className="rounded-xl h-11"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="en">English</SelectItem><SelectItem value="es">Español</SelectItem></SelectContent></Select></div>
                    </div>
                    <DialogFooter><Button type="submit" data-testid="submit-course-button" className="rounded-full h-11 px-6">{t("common.create")}</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((c) => (
                <Card key={c.id} data-testid={`admin-course-card-${c.id}`} className="rounded-[1.4rem] p-6 border-border shadow-[0_6px_24px_rgb(20,38,77,0.05)] space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="h-12 w-12 rounded-2xl bg-primary/5 ring-1 ring-secondary/30 flex items-center justify-center"><BookOpen className="h-6 w-6 text-secondary" /></div>
                    <div className="flex flex-col items-end gap-1.5">
                      <Badge className="rounded-full font-bold">{c.level}</Badge>
                      <Badge variant="outline" className="rounded-full uppercase font-semibold"><Globe className="h-3 w-3 mr-1" />{c.language}</Badge>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold">{c.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.description}</p>
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <ClipboardList className="h-3.5 w-3.5" />{c.lesson_count} lessons · {(c.teachers || []).map((tt) => tt.name).join(", ") || "—"}
                  </div>
                  <Button size="sm" className="rounded-full w-full font-semibold" data-testid={`manage-course-${c.id}`} onClick={() => setEditCourseId(c.id)}>
                    <Layers className="h-4 w-4 mr-1.5" />{t("admin.manage_content")}
                  </Button>
                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
                    <Select onValueChange={(v) => assignTeacher(c.id, v)}>
                      <SelectTrigger className="rounded-xl text-xs" data-testid={`assign-teacher-${c.id}`}><SelectValue placeholder={t("admin.assign_teacher")} /></SelectTrigger>
                      <SelectContent>{teachers.map((tt) => <SelectItem key={tt.id} value={tt.id}>{tt.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select onValueChange={(v) => assignStudent(c.id, v)}>
                      <SelectTrigger className="rounded-xl text-xs" data-testid={`assign-student-${c.id}`}><SelectValue placeholder={t("admin.assign_student")} /></SelectTrigger>
                      <SelectContent>{students.map((st) => <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </Card>
              ))}
              {courses.length === 0 && <p className="text-muted-foreground col-span-full text-center py-10">—</p>}
            </div>
          </TabsContent>

          {/* PROGRESS */}
          <TabsContent value="progress" className="space-y-7">
            <SectionHeader icon={TrendingUp} title={t("admin.progress_title")} sub={t("admin.progress_sub")} />
            <div data-testid="admin-progress-table" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {progress.map((p) => (
                <Card key={p.student.id} data-testid={`progress-row-${p.student.username}`} className="rounded-[1.4rem] p-6 border-border shadow-[0_6px_24px_rgb(20,38,77,0.05)]">
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-2xl bg-primary text-primary-foreground font-heading font-bold flex items-center justify-center shrink-0 ring-2 ring-secondary/40">{initials(p.student.name)}</div>
                    <div className="min-w-0">
                      <h3 className="font-heading font-bold text-lg truncate">{p.student.name}</h3>
                      <p className="text-sm text-muted-foreground truncate">@{p.student.username}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-5 text-center">
                    <div className="rounded-xl bg-muted p-3">
                      <div className="font-heading text-2xl font-extrabold text-primary">{p.submissions}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{t("admin.submissions")}</div>
                    </div>
                    <div className="rounded-xl bg-muted p-3">
                      <div className="font-heading text-2xl font-extrabold text-secondary">{p.average_score ?? "—"}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{t("admin.avg_score")}</div>
                    </div>
                    <div className="rounded-xl bg-muted p-3 flex flex-col items-center justify-center">
                      {p.latest_grade ? <Badge variant="secondary" className="rounded-full text-[10px]">{t(`grade.${p.latest_grade}`)}</Badge> : <span className="text-muted-foreground">—</span>}
                      <div className="text-[11px] text-muted-foreground mt-1">{t("admin.latest")}</div>
                    </div>
                  </div>
                </Card>
              ))}
              {progress.length === 0 && <p className="text-muted-foreground col-span-full text-center py-10">—</p>}
            </div>
          </TabsContent>
        </Tabs>
      </motion.div>

      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="text-2xl">{t("common.reset")}{resetTarget ? ` — @${resetTarget.username}` : ""}</DialogTitle></DialogHeader>
          <div className="space-y-2"><Label>{t("common.password")}</Label>
            <Input data-testid="reset-password-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="rounded-xl h-11" /></div>
          <DialogFooter><Button data-testid="submit-reset-password-button" disabled={!newPassword} onClick={doReset} className="rounded-full h-11 px-6">{t("common.reset")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <CourseEditor courseId={editCourseId} onClose={() => { setEditCourseId(null); load(); }} />
    </AppLayout>
  );
}
